"use client";

import React, { useState } from "react";

interface QuickOrderProps {
  data: {
    badge_text?: string;
    title?: string;
    subtitle?: string;
    product_name?: string;
    button_text?: string;
  };
}

export function QuickOrderBlock({ data }: QuickOrderProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const apiUrl = `http://${window.location.hostname}:8000/api/website/quick-order`;
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name,
          phone,
          product_name: data?.product_name || "Швидке замовлення",
          notes,
        }),
      });

      const resData = await res.json();

      if (!res.ok) {
        throw new Error(
          resData.message || "Помилка при оформленні замовлення.",
        );
      }

      setSuccess(resData.message || "Дякуємо! Ваше замовлення прийнято.");
      setName("");
      setPhone("");
      setNotes("");
    } catch (err: any) {
      setError(
        err.message || "Не вдалося відправити замовлення. Спробуйте пізніше.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section-fluid-padding max-w-xl mx-auto">
      <div className="card-tenant p-8 sm:p-10 shadow-lg">
        {/* Бейдж */}
        <div className="text-center mb-3">
          <span className="badge-status active">
            {data?.badge_text || "⚡ Швидке замовлення"}
          </span>
        </div>

        {/* Заголовок і підказка */}
        <div className="text-center mb-8">
          <h2 className="heading-2 text-gray-900 mb-2">
            {data?.title || "Замовлення в 1 клік"}
          </h2>
          <p className="text-sm text-gray-500">
            {data?.subtitle ||
              "Залиште ваш номер телефону, і менеджер зв'яжеться для уточнення деталей"}
          </p>
          {data?.product_name && (
            <div className="mt-3 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full border border-blue-200">
              Товар: {data.product_name}
            </div>
          )}
        </div>

        {success ? (
          <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded-xl text-center">
            <div className="text-3xl mb-2">🎉</div>
            <h3 className="font-bold text-lg mb-1">Замовлення оформлено!</h3>
            <p className="text-sm">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-200">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Ваше ім&apos;я *
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Олександр"
                className="input-tenant"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Номер телефону *
              </label>
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+380 (99) 000-00-00"
                className="input-tenant"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Коментар (розмір, колір, доставка)
              </label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Розмір M, чорний колір..."
                className="input-tenant"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full py-4 text-base font-semibold shadow-md mt-2"
            >
              {submitting
                ? "Відправка..."
                : data?.button_text || "Оформити замовлення"}
            </button>

            <p className="text-center text-xs text-gray-400 mt-3">
              🔒 Ваші контактні дані захищені та не передаються третім особам
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
